'use client';

import React, { useMemo, createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { Citation, IDataPoolEntry } from '@/types';
import AddressIdenticon from '@/_components/AddressIdenticon/AddressIdenticon';
import VerifiedBadge from '@/_components/VerifiedBadge/VerifiedBadge';
import IdentityTooltip from '@/_components/IdentityTooltip/IdentityTooltip';
import CitationTooltip from '@/_components/CitationTooltip/CitationTooltip';
import { useIdentityQuery } from '@/_state/queries';
import { isValidSS58Address } from '@/_utils/ss58';

interface MarkdownViewerProps {
	content: string;
	className?: string;
	citations?: Citation[];
	dataPoolEntries?: IDataPoolEntry[] | null;
	onCitationClick?: (citationIndex: number) => void;
	onRequestDetails?: () => void;
}

// Context to pass citation processing to all components
interface CitationContextValue {
	validIndices: Set<number>;
	onCitationClick?: (index: number) => void;
	citations?: Citation[];
	dataPoolEntries?: IDataPoolEntry[] | null;
	onRequestDetails?: () => void;
}

const CitationContext = createContext<CitationContextValue>({ validIndices: new Set() });

// Citation button component with hover tooltip
function CitationButton({
	index,
	onClick,
	citation,
	dataPoolEntries
}: {
	index: number;
	onClick: () => void;
	citation?: Citation;
	dataPoolEntries?: IDataPoolEntry[] | null;
}) {
	const { onRequestDetails } = useContext(CitationContext);

	const button = (
		<button
			type='button'
			onClick={onClick}
			className='mx-0.5 inline-flex h-5 min-w-[1.25rem] cursor-pointer items-center justify-center rounded bg-blue-500/20 px-1 align-baseline font-mono text-[10px] font-bold text-blue-600 transition-colors hover:bg-blue-500/40 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
		>
			{index}
		</button>
	);

	// Always wrap with tooltip when citation exists (shows skeleton if data not yet loaded)
	if (citation) {
		return (
			<CitationTooltip
				citation={citation}
				dataPoolEntries={dataPoolEntries ?? undefined}
				onRequestDetails={onRequestDetails}
			>
				{button}
			</CitationTooltip>
		);
	}

	return button;
}

/**
 * Truncate an address to show first 6 and last 4 characters
 */
function truncateAddress(address: string): string {
	if (address.length <= 12) return address;
	return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// Address display component with identicon and identity lookup
function AddressDisplay({ address }: { address: string }) {
	// Fetch on-chain identity - shows address immediately while loading (per user feedback)
	const { data: identity } = useIdentityQuery(address);

	const handleCopy = () => {
		navigator.clipboard.writeText(address);
		const displayText = identity?.displayName || `${address.slice(0, 12)}...${address.slice(-8)}`;
		toast.success('Address copied to clipboard', {
			description: displayText,
			duration: 2000
		});
	};

	// Base content for the address display
	const addressContent = (
		<span
			className='bg-secondary/60 text-foreground hover:bg-accent/60 mx-0.5 inline-flex max-w-full cursor-pointer items-center gap-1 overflow-hidden rounded px-1.5 py-0.5 font-mono text-xs transition-colors sm:text-sm'
			style={{ verticalAlign: 'middle' }}
			onClick={handleCopy}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					handleCopy();
				}
			}}
			role='button'
			tabIndex={0}
		>
			<AddressIdenticon
				address={address}
				size={18}
				className='pointer-events-none flex-shrink-0'
			/>
			{identity?.displayName ? (
				<>
					<span className='text-foreground truncate font-medium'>{identity.displayName}</span>
					{identity.isVerified && identity.judgement && (
						<VerifiedBadge
							judgement={identity.judgement}
							size={14}
						/>
					)}
					<span className='text-muted-foreground flex-shrink-0 text-xs'>({truncateAddress(address)})</span>
				</>
			) : (
				<span className='text-foreground truncate'>{truncateAddress(address)}</span>
			)}
		</span>
	);

	// Wrap with IdentityTooltip if identity is available
	if (identity?.displayName) {
		return <IdentityTooltip identity={identity}>{addressContent}</IdentityTooltip>;
	}

	return addressContent;
}

// Regex to find SS58 addresses in text (standalone, not inside other patterns)
// Matches addresses that are either at word boundaries or surrounded by whitespace/punctuation
const ADDRESS_IN_TEXT_REGEX = /(?<![1-9A-HJ-NP-Za-km-z])([1-9A-HJ-NP-Za-km-z]{46,48})(?![1-9A-HJ-NP-Za-km-z])/g;

// Process text to replace SS58 addresses with identicon + address display
function processTextWithAddresses(text: string): React.ReactNode {
	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let match = ADDRESS_IN_TEXT_REGEX.exec(text);
	let keyCounter = 0;

	// Reset regex state
	ADDRESS_IN_TEXT_REGEX.lastIndex = 0;
	match = ADDRESS_IN_TEXT_REGEX.exec(text);

	while (match !== null) {
		const potentialAddress = match[1];

		// Verify it's a valid SS58 address
		if (isValidSS58Address(potentialAddress)) {
			// Add text before this address
			if (match.index > lastIndex) {
				parts.push(text.slice(lastIndex, match.index));
			}

			keyCounter += 1;
			parts.push(
				<AddressDisplay
					key={`addr-${keyCounter}`}
					address={potentialAddress}
				/>
			);
			lastIndex = match.index + match[0].length;
		}

		match = ADDRESS_IN_TEXT_REGEX.exec(text);
	}

	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	// If no addresses found, return original text
	if (parts.length === 0) return text;
	if (parts.length === 1 && typeof parts[0] === 'string') return parts[0];
	return parts;
}

// Process text to replace citation markers with React elements
// Handles both single [1] and multiple [1, 2, 3] citation formats
function processTextWithCitations({ ctx, text }: { ctx: CitationContextValue; text: string }): React.ReactNode {
	const { validIndices, onCitationClick, citations, dataPoolEntries } = ctx;

	if (!onCitationClick || validIndices.size === 0) {
		return text;
	}

	const parts: React.ReactNode[] = [];
	// Match both [1] and [1, 2, 3] formats
	// eslint-disable-next-line security/detect-unsafe-regex -- safe: no catastrophic backtracking risk
	const citationRegex = /\[(\d+(?:\s*,\s*\d+)*)\]/g;
	let lastIndex = 0;
	let match = citationRegex.exec(text);
	let keyCounter = 0;

	while (match !== null) {
		// Add text before this citation
		if (match.index > lastIndex) {
			parts.push(text.slice(lastIndex, match.index));
		}

		// Parse all citation indices from the match (handles "6" or "6, 7" or "1, 2, 3")
		const citationIndices = match[1].split(',').map((s) => parseInt(s.trim(), 10));
		const validCitationIndices = citationIndices.filter((idx) => validIndices.has(idx));

		if (validCitationIndices.length > 0) {
			// Add citation buttons for each valid citation
			validCitationIndices.forEach((citationIndex, idx) => {
				keyCounter += 1;
				const citation = citations?.find((c) => c.index === citationIndex);
				parts.push(
					<CitationButton
						key={`cite-${citationIndex}-${keyCounter}`}
						index={citationIndex}
						onClick={() => onCitationClick(citationIndex)}
						citation={citation}
						dataPoolEntries={dataPoolEntries}
					/>
				);
				// Add space between multiple citations (but not after the last one)
				if (idx < validCitationIndices.length - 1) {
					parts.push(' ');
				}
			});
		} else {
			// Keep as text if no valid references
			parts.push(match[0]);
		}

		lastIndex = match.index + match[0].length;
		match = citationRegex.exec(text);
	}

	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	if (parts.length === 0) return null;
	if (parts.length === 1) return parts[0];
	return parts;
}

// Combined text processor: first handles addresses, then citations
function processText({ ctx, text }: { ctx: CitationContextValue; text: string }): React.ReactNode {
	// First pass: process addresses
	const addressProcessed = processTextWithAddresses(text);

	// If no addresses were found (returned original string), process citations on the whole text
	if (typeof addressProcessed === 'string') {
		return processTextWithCitations({ ctx, text: addressProcessed });
	}

	// If addresses were found (returned array), process citations on each text segment
	if (Array.isArray(addressProcessed)) {
		return addressProcessed.map((part, idx) => {
			if (typeof part === 'string') {
				const citationProcessed = processTextWithCitations({ ctx, text: part });
				if (typeof citationProcessed === 'string') return citationProcessed;
				if (Array.isArray(citationProcessed)) {
					return <React.Fragment key={`text-${idx}`}>{citationProcessed}</React.Fragment>;
				}
				return citationProcessed;
			}
			// React elements (AddressDisplay components) pass through
			return part;
		});
	}

	// Single non-string element
	return addressProcessed;
}

// Recursively process children to inject citations and address identicons
function processChildren({ ctx, children }: { ctx: CitationContextValue; children: React.ReactNode }): React.ReactNode {
	if (typeof children === 'string') {
		return processText({ ctx, text: children });
	}

	if (Array.isArray(children)) {
		return children.map((child, idx) => {
			if (typeof child === 'string') {
				const processed = processText({ ctx, text: child });
				if (typeof processed === 'string') return processed;
				if (Array.isArray(processed)) {
					return <React.Fragment key={idx}>{processed}</React.Fragment>;
				}
				return processed;
			}
			if (React.isValidElement(child)) {
				const childProps = child.props as { children?: React.ReactNode };
				return React.cloneElement(child as React.ReactElement<{ children?: React.ReactNode }>, {
					...childProps,
					key: child.key || idx,
					children: processChildren({ ctx, children: childProps.children })
				});
			}
			return child;
		});
	}

	if (React.isValidElement(children)) {
		const childProps = children.props as { children?: React.ReactNode };
		return React.cloneElement(children as React.ReactElement<{ children?: React.ReactNode }>, {
			...childProps,
			children: processChildren({ ctx, children: childProps.children })
		});
	}

	return children;
}

// Helper component that processes its children for citations
function WithCitations({ children }: { children?: React.ReactNode }) {
	const ctx = useContext(CitationContext);
	const processed = processChildren({ ctx, children });

	// Return processed content - wrap in fragment if it's an array or complex content
	// This is needed because React components must return a single element

	return <>{processed}</>;
}

// Component definitions (outside render)
const markdownComponents = {
	// Tables
	table: ({ children }: { children?: React.ReactNode }) => (
		<div className='my-4 overflow-x-auto'>
			<table className='bg-bg-default-100 border-border min-w-full border-collapse rounded-lg border'>
				<WithCitations>{children}</WithCitations>
			</table>
		</div>
	),
	thead: ({ children }: { children?: React.ReactNode }) => (
		<thead className='border-border border-b'>
			<WithCitations>{children}</WithCitations>
		</thead>
	),
	tbody: ({ children }: { children?: React.ReactNode }) => (
		<tbody>
			<WithCitations>{children}</WithCitations>
		</tbody>
	),
	tr: ({ children }: { children?: React.ReactNode }) => (
		<tr className='hover:bg-default-100 border-border border-b transition-colors'>
			<WithCitations>{children}</WithCitations>
		</tr>
	),
	th: ({ children }: { children?: React.ReactNode }) => (
		<th className='border-border text-foreground border px-4 py-3 text-left text-sm font-semibold'>
			<WithCitations>{children}</WithCitations>
		</th>
	),
	td: ({ children }: { children?: React.ReactNode }) => (
		<td className='border-border text-foreground border px-4 py-3 font-mono text-sm'>
			<WithCitations>{children}</WithCitations>
		</td>
	),
	// Code blocks
	pre: ({ children }: { children?: React.ReactNode }) => (
		<pre className='border-border bg-code-block-bg my-4 overflow-x-auto rounded-md border p-4'>{children}</pre>
	),
	code: ({ inline, children, className }: { inline?: boolean; children?: React.ReactNode; className?: string }) => {
		if (inline) {
			return (
				<code className='bg-code-inline-bg text-code-inline-text rounded px-2 py-1 font-mono text-sm'>
					<WithCitations>{children}</WithCitations>
				</code>
			);
		}
		return <code className={`text-code-block-text font-mono text-sm ${className || ''}`}>{children}</code>;
	},
	// Headers
	h1: ({ children }: { children?: React.ReactNode }) => (
		<h1 className='border-border text-foreground mt-6 mb-4 border-b pb-2 text-2xl font-bold'>
			<WithCitations>{children}</WithCitations>
		</h1>
	),
	h2: ({ children }: { children?: React.ReactNode }) => (
		<h2 className='text-foreground mt-5 mb-3 text-xl font-semibold'>
			<WithCitations>{children}</WithCitations>
		</h2>
	),
	h3: ({ children }: { children?: React.ReactNode }) => (
		<h3 className='text-foreground mt-4 mb-2 text-lg font-medium'>
			<WithCitations>{children}</WithCitations>
		</h3>
	),
	// Lists
	ul: ({ children }: { children?: React.ReactNode }) => (
		<ul className='text-foreground my-3 list-disc space-y-1 pl-6'>
			<WithCitations>{children}</WithCitations>
		</ul>
	),
	ol: ({ children }: { children?: React.ReactNode }) => (
		<ol className='text-foreground my-3 list-decimal space-y-1 pl-6'>
			<WithCitations>{children}</WithCitations>
		</ol>
	),
	li: ({ children }: { children?: React.ReactNode }) => (
		<li className='text-foreground'>
			<WithCitations>{children}</WithCitations>
		</li>
	),
	// Paragraphs - using div instead of p to allow block-level elements (like Identicon's div)
	p: ({ children }: { children?: React.ReactNode }) => (
		<div className='text-foreground mb-4 leading-relaxed'>
			<WithCitations>{children}</WithCitations>
		</div>
	),
	// Links
	a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
		<a
			href={href}
			className='text-blue-600 underline transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
			target='_blank'
			rel='noopener noreferrer'
		>
			<WithCitations>{children}</WithCitations>
		</a>
	),
	// Blockquotes
	blockquote: ({ children }: { children?: React.ReactNode }) => (
		<blockquote className='border-blockquote-border bg-blockquote-bg text-blockquote-text my-4 border-l-4 py-2 pl-4 italic'>
			<WithCitations>{children}</WithCitations>
		</blockquote>
	),
	// Horizontal rule
	hr: () => <hr className='border-border my-6' />,
	// Strikethrough
	del: ({ children }: { children?: React.ReactNode }) => (
		<del className='text-muted-foreground line-through'>
			<WithCitations>{children}</WithCitations>
		</del>
	),
	// Task lists
	input: ({ type, checked, disabled }: { type?: string; checked?: boolean; disabled?: boolean }) => (
		<input
			type={type}
			checked={checked}
			disabled={disabled}
			className='mr-2 accent-blue-500'
		/>
	),
	// Strong/Bold
	strong: ({ children }: { children?: React.ReactNode }) => (
		<strong className='font-bold'>
			<WithCitations>{children}</WithCitations>
		</strong>
	),
	// Emphasis/Italic
	em: ({ children }: { children?: React.ReactNode }) => (
		<em className='italic'>
			<WithCitations>{children}</WithCitations>
		</em>
	)
};

function MarkdownViewer({
	content,
	className = '',
	citations,
	dataPoolEntries,
	onCitationClick,
	onRequestDetails
}: MarkdownViewerProps) {
	// Create context value
	const citationContextValue = useMemo(
		() => ({
			validIndices: new Set(citations?.map((c) => c.index) || []),
			onCitationClick,
			citations,
			dataPoolEntries,
			onRequestDetails
		}),
		[citations, dataPoolEntries, onCitationClick, onRequestDetails]
	);

	return (
		<CitationContext.Provider value={citationContextValue}>
			<div className={`prose dark:prose-invert max-w-none ${className}`}>
				<ReactMarkdown
					remarkPlugins={[remarkGfm]}
					components={markdownComponents}
				>
					{content}
				</ReactMarkdown>
			</div>
		</CitationContext.Provider>
	);
}

export default MarkdownViewer;
